import type { Conversation } from '@twilio/conversations';

import { useChatStore } from '@south-street-app/stores';

type ChatConversationAttributes = {
  type?: 'SUPPORT' | 'TRADE' | 'BROADCAST' | string;
  status?: 'pending' | 'resolved' | null;
  assignedTo?: string | null;
  endUserId?: string | null;
};

type ConversationPreview = {
  body: string;
  dateCreated: Date | null;
};

const RESOLVED_CONVERSATION_MESSAGE_PREFIX = 'Issue resolved by';
const RESOLVED_CONVERSATION_MESSAGE_TEXT = 'Marked as resolved';
const UNREAD_FALLBACK_PAGE_SIZE = 100;

const SUPPORT_UNIQUE_NAME_PREFIX = 'SUPPORT:';
const TRADE_UNIQUE_NAME_PREFIX = 'TRADE:';
const BROADCAST_UPDATES_UNIQUE_NAME = 'BROADCAST:UPDATES';

const getConversationAttributes = (
  conversation: Conversation | null | undefined,
): ChatConversationAttributes => {
  if (!conversation) {
    return {};
  }

  return (conversation.attributes as ChatConversationAttributes | null) ?? {};
};

const isSupportConversation = (
  conversationOrUniqueName: Conversation | string | null | undefined,
) => {
  const uniqueName =
    typeof conversationOrUniqueName === 'string'
      ? conversationOrUniqueName
      : conversationOrUniqueName?.uniqueName;

  if (!uniqueName) {
    return false;
  }

  return (
    uniqueName.startsWith(SUPPORT_UNIQUE_NAME_PREFIX) ||
    getConversationAttributes(
      typeof conversationOrUniqueName === 'string'
        ? undefined
        : conversationOrUniqueName,
    ).type === 'SUPPORT'
  );
};

const isTradeConversation = (
  conversationOrUniqueName: Conversation | string | null | undefined,
) => {
  const uniqueName =
    typeof conversationOrUniqueName === 'string'
      ? conversationOrUniqueName
      : conversationOrUniqueName?.uniqueName;

  if (!uniqueName) {
    return false;
  }

  return (
    uniqueName.startsWith(TRADE_UNIQUE_NAME_PREFIX) ||
    getConversationAttributes(
      typeof conversationOrUniqueName === 'string'
        ? undefined
        : conversationOrUniqueName,
    ).type === 'TRADE'
  );
};

const isUpdatesConversation = (
  conversationOrUniqueName: Conversation | string | null | undefined,
) => {
  const uniqueName =
    typeof conversationOrUniqueName === 'string'
      ? conversationOrUniqueName
      : conversationOrUniqueName?.uniqueName;

  if (!uniqueName) {
    return false;
  }

  return (
    uniqueName === BROADCAST_UPDATES_UNIQUE_NAME ||
    getConversationAttributes(
      typeof conversationOrUniqueName === 'string'
        ? undefined
        : conversationOrUniqueName,
    ).type === 'BROADCAST'
  );
};

const isResolvedConversationMessage = (
  author: string | null,
  body: string | null | undefined,
) => {
  if (!author || typeof body !== 'string') {
    return false;
  }

  return (
    !author.startsWith('END_USER:') &&
    !author.startsWith('STAFF_USER:') &&
    body.startsWith(RESOLVED_CONVERSATION_MESSAGE_PREFIX)
  );
};

const isVisibleConversationMessage = (
  author: string | null,
  body: string | null,
  conversation?: Conversation | null | undefined,
) => {
  if (!body?.trim()) {
    return false;
  }

  if (!author) {
    return false;
  }

  if (isUpdatesConversation(conversation)) {
    return author.startsWith('STAFF_USER:') || author.startsWith('END_USER:');
  }

  return (
    author.startsWith('END_USER:') ||
    author.startsWith('STAFF_USER:') ||
    isResolvedConversationMessage(author, body)
  );
};

const isUnreadStaffMessage = (
  message: {
    author?: string | null;
    body?: string | null;
    index?: number | null;
  },
  conversation: Conversation,
  lastReadMessageIndex: number | null,
) => {
  if (
    !isVisibleConversationMessage(
      message.author ?? null,
      message.body ?? null,
      conversation,
    ) ||
    !message.author?.startsWith('STAFF_USER:')
  ) {
    return false;
  }

  if (lastReadMessageIndex === null) {
    return true;
  }

  return (
    typeof message.index === 'number' && message.index > lastReadMessageIndex
  );
};

const sortArchivedConversations = (conversations: Conversation[]) =>
  [...conversations].sort(
    (left, right) =>
      (right.lastMessage?.dateCreated?.getTime() ?? 0) -
      (left.lastMessage?.dateCreated?.getTime() ?? 0),
  );

const getConversationPreview = async (
  conversation: Conversation | null | undefined,
) => {
  if (!conversation) {
    return null;
  }

  const paginator = await conversation.getMessages(10);

  const previewMessage = [...paginator.items]
    .reverse()
    .find((message) =>
      isVisibleConversationMessage(message.author, message.body, conversation),
    );

  if (!previewMessage?.body) {
    return null;
  }

  return {
    body: isResolvedConversationMessage(
      previewMessage.author,
      previewMessage.body,
    )
      ? RESOLVED_CONVERSATION_MESSAGE_TEXT
      : previewMessage.body,
    dateCreated: previewMessage.dateCreated ?? null,
  } satisfies ConversationPreview;
};

const countUnreadFallbackMessages = async (conversation: Conversation) => {
  let paginator = await conversation.getMessages(UNREAD_FALLBACK_PAGE_SIZE);
  const lastReadMessageIndex = conversation.lastReadMessageIndex;
  let unreadMessageCount = paginator.items.filter((message) =>
    isUnreadStaffMessage(message, conversation, lastReadMessageIndex),
  ).length;

  while (paginator.hasNextPage) {
    paginator = await paginator.nextPage();

    unreadMessageCount += paginator.items.filter((message) =>
      isUnreadStaffMessage(message, conversation, lastReadMessageIndex),
    ).length;
  }

  return unreadMessageCount;
};

const hasPotentialUnreadStaffMessage = (conversation: Conversation) => {
  const lastReadMessageIndex = conversation.lastReadMessageIndex;
  const lastMessage = conversation.lastMessage;

  if (!lastMessage) {
    return false;
  }

  return isUnreadStaffMessage(lastMessage, conversation, lastReadMessageIndex);
};

const waitForReadHorizon = async (
  conversation: Conversation,
  targetIndex: number,
) => {
  const deadline = Date.now() + 2_000;

  while (Date.now() < deadline) {
    if ((conversation.lastReadMessageIndex ?? -1) >= targetIndex) {
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, 50));
  }
};

const resolveUnreadCount = async (
  conversation: Conversation | null | undefined,
) => {
  if (!conversation) {
    return 0;
  }

  const unreadCount = await conversation.getUnreadMessagesCount();

  if (typeof unreadCount === 'number' && unreadCount > 0) {
    return unreadCount;
  }

  if (typeof unreadCount === 'number' && unreadCount === 0) {
    if (!hasPotentialUnreadStaffMessage(conversation)) {
      return 0;
    }
  }

  return countUnreadFallbackMessages(conversation);
};

const refreshUnreadCounts = async () => {
  const {
    supportConversation,
    updatesConversation,
    setSupportUnreadCount,
    setUpdatesUnreadCount,
  } = useChatStore.getState();

  const [supportUnreadCount, updatesUnreadCount] = await Promise.all([
    resolveUnreadCount(supportConversation),
    resolveUnreadCount(updatesConversation),
  ]);

  setSupportUnreadCount(supportUnreadCount);

  setUpdatesUnreadCount(updatesUnreadCount);
};

const markConversationRead = async (
  conversation: Conversation | null | undefined,
) => {
  if (!conversation) {
    return;
  }

  const latestMessageIndex = conversation.lastMessage?.index;

  await conversation.setAllMessagesRead();

  if (
    typeof latestMessageIndex === 'number' &&
    (conversation.lastReadMessageIndex ?? -1) < latestMessageIndex
  ) {
    await conversation
      .advanceLastReadMessageIndex(latestMessageIndex)
      .catch(() => undefined);

    await waitForReadHorizon(conversation, latestMessageIndex);
  }

  await refreshUnreadCounts();
};

export {
  BROADCAST_UPDATES_UNIQUE_NAME,
  RESOLVED_CONVERSATION_MESSAGE_TEXT,
  SUPPORT_UNIQUE_NAME_PREFIX,
  TRADE_UNIQUE_NAME_PREFIX,
  getConversationAttributes,
  getConversationPreview,
  isResolvedConversationMessage,
  isSupportConversation,
  isTradeConversation,
  isUpdatesConversation,
  isVisibleConversationMessage,
  markConversationRead,
  refreshUnreadCounts,
  resolveUnreadCount,
  sortArchivedConversations,
};
export type { ChatConversationAttributes, ConversationPreview };
